/**
 * IMPORTANT — this file is imported directly by a backend test:
 * `backend/src/services/defectSeverity/__tests__/defectSeverity.test.js`
 * imports `FAILURE_OPTIONS` from here (by relative path, deliberately — see
 * `ARCHITECTURE.md` §3a) as the cross-package contract test proving the
 * frontend's option strings and the backend's matcher never drift apart.
 *
 * That means this file (and anything it imports, transitively) must stay
 * free of React, browser globals, and any other third-party/runtime-only
 * import — Backend CI runs this under plain Node/Jest, not a bundler or
 * jsdom. Adding one breaks Backend CI with a module-not-found or a browser-
 * API error pointing at a file that looks like it has nothing to do with
 * the backend. Currently this file only imports from `../lib/testValidators`
 * (also plain JS, no third-party imports) — keep it that way, or move
 * `FAILURE_OPTIONS` somewhere the contract test can reach without pulling
 * in anything heavier.
 */
import { DID_NOT_TRIP_DEFECT } from '../lib/testValidators';

/**
 * Asset type -> test type options for recording tests.
 * Each option: { value, label } or { value, label, requiresMetrics, metricsLabel }.
 * Keys must be snake_case (e.g. exit_light, distribution_board).
 */
export const ASSET_TEST_TYPES = {
  rcd: [
    { value: 'Push Button', label: 'Push Button' },
    { value: 'Operating Time', label: 'Operating Time', requiresMetrics: true, metricsLabel: 'Trip Time (ms)' },
  ],
  exit_light: [
    { value: '90-minute Discharge', label: '90-minute Discharge' },
  ],
  emergency_light: [
    { value: '90-minute Discharge', label: '90-minute Discharge' },
  ],
  duress_button: [
    { value: 'Functional', label: 'Functional' },
  ],
  distribution_board: [
    { value: 'Visual Check', label: 'Visual Check' },
  ],
  ceiling_fan: [
    { value: 'Operational Check', label: 'Operational Check' },
  ],
  wall_heater: [
    { value: 'Operational Check', label: 'Operational Check' },
  ],
  automated_gate: [
    { value: 'Operational Check', label: 'Operational Check' },
  ],
  external_lighting_control: [
    { value: 'Operational Check', label: 'Operational Check' },
  ],
  workshop_equipment: [
    { value: 'Inspection', label: 'Inspection' },
  ],
  emergency_light_test_kit: [
    { value: 'Functional', label: 'Functional' },
  ],
};

export const ELEMENT_TYPE_OPTIONS = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'fitments', label: 'Workshop Equipment' },
];

/** Human label for session element_type; falls back to raw value for types not yet in ELEMENT_TYPE_OPTIONS. */
export function getElementTypeLabel(elementType) {
  const key = String(elementType || 'electrical').trim().toLowerCase();
  const found = ELEMENT_TYPE_OPTIONS.find((o) => o.value === key);
  return found?.label ?? (elementType ? String(elementType).trim() : 'Inspection');
}

/** Sort in-progress session rows for stable UI (defined element types first, then unknown). */
export function sortSessionsByElementTypeOrder(sessions) {
  const order = ELEMENT_TYPE_OPTIONS.map((o) => o.value);
  return [...sessions].sort((a, b) => {
    const etA = String(a.element_type || 'electrical').toLowerCase();
    const etB = String(b.element_type || 'electrical').toLowerCase();
    const idxA = order.indexOf(etA);
    const idxB = order.indexOf(etB);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
}

const SESSION_ASSET_OPTIONS_ELECTRICAL = [
  { value: 'combined_exit_emergency', label: 'Exit & Emergency Lights' },
  // Plain entry, not folded into combined_exit_emergency: it groups with
  // exit/emergency work in practice (same technician visit, same standard),
  // but it is its own asset type with its own failure options and generic
  // submit path — not a luminaire, so it does not join that combined filter.
  { value: 'emergency_light_test_kit', label: 'Emergency Light Test Kit' },
  { value: 'combined_db_rcd', label: 'Distribution Boards & RCDs' },
  { value: 'combined_fan_heater', label: 'Ceiling Fans & Wall Heaters' },
  { value: 'duress_button', label: 'Duress Button' },
  { value: 'automated_gate', label: 'Automated Gate' },
  { value: 'external_lighting_control', label: 'External Lighting Control' },
];

const SESSION_ASSET_OPTIONS_FITMENTS = [
  { value: 'workshop_equipment', label: 'Workshop Equipment' },
];

export const SESSION_ASSET_OPTIONS_BY_ELEMENT = {
  electrical: SESSION_ASSET_OPTIONS_ELECTRICAL,
  fitments: SESSION_ASSET_OPTIONS_FITMENTS,
};

export function getAssetOptionsForElement(elementType) {
  if (!elementType || typeof elementType !== 'string') return SESSION_ASSET_OPTIONS_ELECTRICAL;
  const key = elementType.trim().toLowerCase();
  return SESSION_ASSET_OPTIONS_BY_ELEMENT[key] ?? SESSION_ASSET_OPTIONS_ELECTRICAL;
}

export const WORKSHOP_CHECKLIST = [
  { key: 'emergency_stop', label: 'Emergency Stop', hint: 'Accessible, correct red/yellow colour, activates correctly, resets correctly' },
  { key: 'safety_guards', label: 'Safety Guards', hint: 'In place, secure, undamaged, interlock functions if fitted' },
  { key: 'control_components', label: 'Control Components', hint: 'Start/stop switches functional, no damage, correct labelling' },
  { key: 'power_supply_leads', label: 'Power Supply & Leads', hint: 'No fraying, damage, or exposed conductors, secure connection, correct rating' },
  { key: 'blades_cutting', label: 'Blades / Cutting Components', hint: 'Secure, undamaged, correct for machine (where applicable)' },
  { key: 'structural_integrity', label: 'Structural Integrity', hint: 'No cracks, loose bolts, excessive vibration or unusual noise during operation' },
  { key: 'hazard_labelling', label: 'Hazard Labelling', hint: 'Warning labels present and legible, PPE requirements posted' },
];

const DEFAULT_FALLBACK_OPTIONS = [{ value: 'Functional', label: 'Functional' }];

/**
 * Normalize asset type for lookup: trim, lowercase, replace spaces with underscores.
 * API/DB may return "exit_light" or occasionally "Exit Light" etc.
 */
export function normalizeAssetType(assetType) {
  if (assetType == null || typeof assetType !== 'string') return '';
  return assetType.trim().toLowerCase().replace(/\s+/g, '_');
}

/**
 * Get test type options for an asset type. Uses ASSET_TEST_TYPES with normalized key lookup.
 * @param {string} assetType - e.g. 'rcd', 'exit_light', 'Exit Light'
 * @returns {Array<{ value: string, label: string, requiresMetrics?: boolean, metricsLabel?: string }>}
 */
export function getTestOptionsForAssetType(assetType) {
  const key = normalizeAssetType(assetType);
  if (!key) return DEFAULT_FALLBACK_OPTIONS;
  const options = ASSET_TEST_TYPES[key];
  if (options && Array.isArray(options) && options.length > 0) {
    return options;
  }
  return DEFAULT_FALLBACK_OPTIONS;
}

export const RESULT_OPTIONS = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'needs_repair', label: 'Needs Repair' },
];

/**
 * Structured failure reasons by asset type — Brief 36's enriched lists.
 * Keys are normalized (lowercase, underscores). Fallback used for unknown types.
 *
 * Every option string here is a contract with `backend/src/services/
 * defectSeverity/index.js`'s `toFailureMode` — that adapter matches these
 * exact strings, character for character. Changing a string here without
 * updating the matching entry there makes a defect quietly resolve to
 * Major instead of its real tier, with no error and no failing test unless
 * one covers that exact pair. The contract test in
 * `defectSeverity.test.js` is what actually enforces this; do not rely on
 * reading the two files side by side.
 *
 * The generic strings this brief removed ('Visual Defect', 'Operational
 * Failure', 'Noise/Vibration Issue' as a catch-all) are intentionally gone
 * from every enriched type below — only `'Other'` remains as the escape
 * hatch (resolves to Major, flagged). They are still matched as legacy
 * strings in the backend adapter, because old `test_results` rows carry
 * them and there is no migration; see that file's docblock.
 *
 * The comma rule (Brief 37, see `ISSUES.md`'s "Known residual edge case —
 * splitReasons" entry, 13/09/2026): no option string below, and no string
 * any save path writes into `defects_found`, may contain a literal comma.
 * Every asset type here is single-select, so `defects_found` is normally
 * either one exact option string or several joined with `', '` — a comma
 * inside one option would make it indistinguishable from two. RCD is the
 * one asset type that still joins a selected reason with derived measurement
 * reasons (see `recordTestPayload.js`), so it is the type where that logged
 * edge case is actually reachable; the other enriched types below carry
 * comma-bearing strings safely only because they stay single-select.
 */
export const FAILURE_OPTIONS = {
  exit_light: [
    'Failed Duration Test (<90 min)',
    'Charge Indicator / Lamp Failure',
    'Damaged — Live Conductors Accessible',
    'Diffuser Cracked or Missing',
    'Legend Not Legible',
    'Other',
  ],
  emergency_light: [
    'Failed Duration Test (<90 min)',
    'Charge Indicator / Lamp Failure',
    'Damaged — Live Conductors Accessible',
    'Diffuser Cracked or Missing',
    'Other',
  ],
  // Brief 37: the visual pass/fail gate is gone. `Trip Time Failure (>300ms)`
  // and `Push Button Mechanical Failure` are derived from the measurements,
  // never chosen — see `buildRecordTestPayload`'s RCD branch. `Did Not Trip`
  // is a selectable reason now, not a checkbox. `Visual Defect` stays
  // matched in the backend adapter as legacy (old records carry it, no
  // migration) but is no longer offered here.
  rcd: [
    DID_NOT_TRIP_DEFECT,
    'Poor Condition',
    'Poor Fitment',
    'Other',
  ],
  distribution_board: [
    'Exposed Live Parts / Escutcheon Missing or Damaged',
    'Hot Joint / Thermal Issue',
    'Circuit Labelling Missing or Incorrect',
    'Clearance / Access Issue',
    'Other',
  ],
  automated_gate: [
    'Does Not Reverse or Stop on Obstruction',
    'Manual Release Inoperative',
    'Slow, Noisy or Misaligned',
    'Debris Blocking Gate',
    'Other',
  ],
  duress_button: [
    'Does Not Signal on Activation',
    'Housing Loose or Damaged (Still Signals)',
    'Other',
  ],
  ceiling_fan: [
    'Mounting Loose or Insecure',
    'Damaged Blades',
    'Bearing Noise, Vibration or Wobble',
    'Other',
  ],
  wall_heater: [
    'Guard Loose, Damaged or Missing',
    'Does Not Heat / Thermostat Fault',
    'Other',
  ],
  emergency_light_test_kit: [
    'Not Operational',
    'Timer or Controller Fault',
    'Damaged — Live Conductors Accessible',
    'Labelling Missing or Incorrect',
    'Other',
  ],
  external_lighting_control: [
    'Non-Operational',
    'Control or Switching Configuration Incorrect',
    'Programmed Times Require Adjustment',
    'Other',
  ],
  _default: ['Other', 'Visual Defect', 'Operational Failure'],
};

/** Get failure reason options for an asset type. */
export function getFailureOptionsForAssetType(assetType) {
  const key = normalizeAssetType(assetType);
  return FAILURE_OPTIONS[key] ?? FAILURE_OPTIONS._default;
}

/**
 * `defects_found` may join several reasons with `', '` — split back out.
 *
 * Brief 37 correction: this is a plain comma-split. It does **not** mirror
 * `backend/src/services/defectSeverity/index.js`'s `splitReasons`, which
 * prepends the raw, un-split string as its own candidate so a single
 * reason that itself contains a comma survives intact. That drift is safe
 * here because this function's only caller, `matchSingleFailureOption`
 * below, already checks the whole raw string against the known-options
 * list *before* ever calling this — the comma-bearing-single-reason case
 * never reaches the split path. Do not rely on that same safety in a new
 * caller without re-checking this comment.
 */
export function splitFailureReasons(defectsFound) {
  if (!defectsFound || typeof defectsFound !== 'string') return [];
  return defectsFound.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Match a stored `defects_found` string to exactly one known failure option
 * for the given asset type (the current dropdown list, `'Other'` included).
 * Exact match only, whole string first — some option strings (e.g. "Bearing
 * Noise, Vibration or Wobble") contain a comma themselves, so a naive
 * comma-split on a single-reason string would wrongly look like two.
 *
 * Returns `null` when the text matches zero known options, or more than
 * one (a genuinely ambiguous multi-reason record) — callers must not guess
 * in either case; see `useExistingTestHydration.js` and Brief 36 Part 3.
 */
export function matchSingleFailureOption(defectsFound, assetType) {
  if (!defectsFound || typeof defectsFound !== 'string') return null;
  const raw = defectsFound.trim();
  if (!raw) return null;
  const known = getFailureOptionsForAssetType(assetType);
  if (known.includes(raw)) return raw;
  const reasons = splitFailureReasons(defectsFound);
  if (reasons.length !== 1) return null;
  return known.includes(reasons[0]) ? reasons[0] : null;
}

export function isExitEmergencyAsset(assetType) {
  const t = normalizeAssetType(assetType);
  return t === 'exit_light' || t === 'emergency_light';
}

export function isRcdAsset(assetType) {
  return normalizeAssetType(assetType) === 'rcd';
}

export function isWorkshopAsset(assetType) {
  return normalizeAssetType(assetType) === 'workshop_equipment';
}
